import React, { useState, useEffect } from "react";
import { TextField, Button, MenuItem, Box, Typography, Alert, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase/firebaseConfig";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { logActivity } from "../utils/activityLogger";

const roleOptions = ["Admin", "Manager", "Member"];

// safe Timestamp | Date | string -> Date|null
const toJsDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);

const Login = () => {
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Always start with a clean session
  useEffect(() => {
    getAuth().signOut().catch(() => {});
  }, []);

  const handleLogin = async () => {
    setError("");
    setLoading(true);

    try {
      // 1) Map companyName -> companyId
      const companySnap = await getDocs(
        query(collection(db, "companies"), where("companyName", "==", companyName))
      );
      if (companySnap.empty) throw new Error("Company not found. Please check the name.");
      const companyDoc = companySnap.docs[0];
      const companyIdFromName = companyDoc.id;

      // 2) Sign in
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const user = cred.user;
      await user.getIdToken(true); // refresh token

      // 3) Load user and ensure they belong to that company
      const userDocSnap = await getDoc(doc(db, "users", user.uid));
      if (!userDocSnap.exists()) throw new Error("User record not found.");
      const userData = userDocSnap.data();
      if (userData.companyId !== companyIdFromName) {
        throw new Error("Invalid company name for this user.");
      }

      // 4) License enforcement at LOGIN (authoritative check on /licenses)
      const licSnap = await getDocs(
        query(
          collection(db, "licenses"),
          where("assignedCompanyId", "==", companyIdFromName),
          where("licenseKey", "==", companyDoc.data().licenseKey || "")
        )
      );
      if (licSnap.empty) {
        // As a fallback, try by assignedCompanyId only (in case licenseKey mirror is missing)
        const fallback = await getDocs(
          query(collection(db, "licenses"), where("assignedCompanyId", "==", companyIdFromName))
        );
        if (fallback.empty) throw new Error("License not assigned to this company.");
        // use the first matched license
        const l = fallback.docs[0].data();
        await enforceLicense(l);
      } else {
        const l = licSnap.docs[0].data();
        await enforceLicense(l);
      }

      // 5) Log and go through
      await logActivity({
        userId: user.uid,
        userName: userData.name,
        companyId: userData.companyId,
        action: "LOGIN",
        module: "Authentication",
        details: `User ${userData.name} logged in to company "${companyName}".`,
      });

      navigate("/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Login failed.");
      // If we had signed in already, sign out on failure
      try { await getAuth().signOut(); } catch {}
    } finally {
      setLoading(false);
    }
  };

  // throws if invalid
  async function enforceLicense(lic) {
    const now = new Date();
    const vFrom = toJsDate(lic.validFrom);
    const vTo = toJsDate(lic.validTo);

    if ((lic.status || "active") !== "active") {
      throw new Error("Company license is revoked.");
    }
    if (vFrom && now < vFrom) {
      throw new Error("Company license is not yet valid.");
    }
    if (vTo && now > vTo) {
      throw new Error("Company license has expired.");
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(25,118,210,0.02) 100%)",
        px: 2,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 420, p: 4, borderRadius: 3, boxShadow: 4, backgroundColor: "#ffffff" }}>
        <Typography variant="h4" sx={{ mb: 2, textAlign: "center", fontWeight: 600 }}>
          Welcome to WorkPulse.in
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 4, textAlign: "center", color: "text.secondary" }}>
          Sign in to continue managing your team
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <TextField label="Organization Name" variant="outlined" value={companyName} onChange={(e) => setCompanyName(e.target.value)} fullWidth sx={{ mb: 2 }} />
        <TextField label="Email" variant="outlined" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth sx={{ mb: 2 }} />
        <TextField label="Role" select variant="outlined" value={role} onChange={(e) => setRole(e.target.value)} fullWidth sx={{ mb: 2 }}>
          {roleOptions.map((r) => (<MenuItem key={r} value={r}>{r}</MenuItem>))}
        </TextField>
        <TextField label="Password" type="password" variant="outlined" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth sx={{ mb: 3 }} />

        <Button variant="contained" color="primary" fullWidth onClick={handleLogin} disabled={loading} sx={{ mb: 1 }}>
          {loading ? <CircularProgress size={24} /> : "Login"}
        </Button>
      </Box>
    </Box>
  );
};

export default Login;
